import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";

interface SelectedEmoji {
  char: string;
  code: string;
}

interface EmojiSelectionPanelProps {
  selectedEmojis: SelectedEmoji[];
  requiredCount: number;
  onRemove?: (index: number) => void;
}

export default function EmojiSelectionPanel({
  selectedEmojis,
  requiredCount,
  onRemove,
}: EmojiSelectionPanelProps) {
  const progress = (selectedEmojis.length / requiredCount) * 100;
  const isComplete = selectedEmojis.length === requiredCount;

  return (
    <div className="space-y-3">
      {/* Progress */}
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-gray-700">Selection Progress</h4>
          <Badge variant={isComplete ? "default" : "secondary"} className="text-xs">
            {selectedEmojis.length} / {requiredCount}
          </Badge>
        </div>
        <Progress value={progress} className="h-2" />
        {isComplete ? (
          <p className="text-xs text-green-600 font-medium">✓ Complete! Ready to proceed</p>
        ) : (
          <p className="text-xs text-gray-500">
            Select {requiredCount - selectedEmojis.length} more emoji{requiredCount - selectedEmojis.length !== 1 ? "s" : ""}
          </p>
        )}
      </div>

      {/* Selected Emojis Grid */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">Selected Emojis</h4>
        {selectedEmojis.length > 0 ? (
          <ScrollArea className="h-full">
            <div className="grid grid-cols-6 gap-1.5 pr-4">
              {selectedEmojis.map((emoji, index) => (
                <button
                  key={index}
                  onClick={() => onRemove?.(index)}
                  className="relative flex items-center justify-center h-10 w-10 bg-white border-2 border-purple-200 rounded hover:border-red-400 hover:bg-red-50 transition-all cursor-pointer group"
                  title="Click to remove"
                >
                  <span className="text-xl group-hover:opacity-50 transition-opacity">{emoji.char}</span>
                  <X className="absolute h-3 w-3 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                </button>
              ))}
            </div>
          </ScrollArea>
        ) : (
          <p className="text-sm text-gray-500 text-center py-4">
            No emojis selected yet
          </p>
        )}
      </div>
    </div>
  );
}

